import { UserDetailDto } from '@artemis-software/wr-api';
import { Permission } from '@services/current-user.service';
import TenantTypeEnum = UserDetailDto.TenantTypeEnum;

export type LandingTileAccent = 'red' | 'blue' | 'green' | 'slate' | 'indigo';

export interface LandingTile {
  title: string;
  description: string;
  icon: string;
  accent: LandingTileAccent;
  link: string;
  queryParams?: Record<string, string>;
  permission?: Permission;
  disabled?: boolean;
  dataCy: string;
}

const INTERNAL_TILES: LandingTile[] = [
  {
    title: 'Gebäude',
    description: 'Gebäude verwalten & inspizieren',
    icon: 'house',
    accent: 'red',
    link: '/buildings',
    dataCy: 'landing-tile-buildings',
  },
  {
    title: 'Rundgänge',
    description: 'Begehungen planen & durchführen',
    icon: 'clipboard-check',
    accent: 'blue',
    link: '/inspections',
    dataCy: 'landing-tile-inspections',
  },
  {
    title: 'Wartungskalender',
    description: 'Wartungstermine im Blick behalten',
    icon: 'calendar-days',
    accent: 'green',
    link: '/maintenanceTimers/calendar',
    dataCy: 'landing-tile-maintenance-calendar',
  },
  {
    title: 'Gebäudeausstattung',
    description: 'TGA-Anlagen einsehen & verwalten',
    icon: 'cubes',
    accent: 'slate',
    link: '/buildingEquipments',
    dataCy: 'landing-tile-building-equipments',
  },
  {
    title: 'Gebäude Dashboard',
    description: 'Auswertungen & Übersichten',
    icon: 'diagram-project',
    accent: 'indigo',
    link: '/dashboard/building-new',
    dataCy: 'landing-tile-building-dashboard',
  },
];

const CONTRACTOR_TILES: LandingTile[] = [
  {
    title: 'Meine Gebäude',
    description: 'Zugewiesene Gebäude öffnen',
    icon: 'house',
    accent: 'red',
    link: '/contractorBuildings',
    permission: 'viewContractorBuildings',
    dataCy: 'landing-tile-contractor-buildings',
  },
  {
    title: 'Reparaturaufträge',
    description: 'Aufträge abschließen oder stornieren',
    icon: 'screwdriver-wrench',
    accent: 'blue',
    link: '/contractor/repairTasks',
    dataCy: 'landing-tile-contractor-repair-tasks',
  },
  {
    title: 'Wartungstimer',
    description: 'Fällige Wartungen im Blick behalten',
    icon: 'calendar-days',
    accent: 'green',
    link: '/dashboard/maintenanceTimers',
    permission: 'viewMaintenanceTimerDashboard',
    dataCy: 'landing-tile-contractor-maintenance-timers',
  },
];

const EXTERNAL_TILES: LandingTile[] = [
  {
    title: 'Meine Gebäude',
    description: 'Gebäude meiner Teams',
    icon: 'house-user',
    accent: 'red',
    link: '/external/buildings',
    queryParams: { myTeamOnly: 'true' },
    permission: 'viewExternalBuildings',
    dataCy: 'landing-tile-external-my-buildings',
  },
  {
    title: 'Alle Gebäude',
    description: 'Alle Gebäude des Unternehmens',
    icon: 'city',
    accent: 'blue',
    link: '/external/buildings',
    queryParams: { myTeamOnly: 'false' },
    permission: 'viewExternalBuildings',
    dataCy: 'landing-tile-external-all-buildings',
  },
  {
    title: 'Störungen / Alarme',
    description: 'Demnächst verfügbar',
    icon: 'bell',
    accent: 'slate',
    link: '',
    disabled: true,
    dataCy: 'landing-tile-external-alarms',
  },
  {
    title: 'Wartungstimer',
    description: 'Fällige Wartungen im Blick behalten',
    icon: 'calendar-days',
    accent: 'green',
    link: '/dashboard/maintenanceTimers',
    permission: 'viewMaintenanceTimerDashboard',
    dataCy: 'landing-tile-external-maintenance-timers',
  },
  {
    title: 'Mängel / Schäden lt. B1300 Bericht',
    description: 'Offene Mängel aus dem letzten Bericht',
    icon: 'exclamation-triangle',
    accent: 'indigo',
    link: '/statistics/defect-report',
    permission: 'viewInspectionEntryStatistics',
    dataCy: 'landing-tile-external-defect-report',
  },
];

export function landingTilesFor(tenantType: TenantTypeEnum | undefined): LandingTile[] {
  switch (tenantType) {
    case TenantTypeEnum.Internal:
      return INTERNAL_TILES;
    case TenantTypeEnum.External:
      return EXTERNAL_TILES;
    case TenantTypeEnum.Contractor:
      return CONTRACTOR_TILES;
    default:
      return [];
  }
}
